import React, { useMemo } from 'react';
import { differenceInDays } from 'date-fns';
import { View } from 'react-native';
import Button from 'design-system/Button';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import Status from 'design-system/Status';
import useStyles from 'hooks/useStyles';
import RentReportingStatus from 'features/rent-reporting/components/RentReportingStatus';
import createStyleSheets from 'utils/createStyleSheets';
import { formatPlural } from 'utils';
import { canRenewConnection, isConnectionBroken } from '../utils';
import BankConnectionStatus from './BankConnectionStatus';
const strings = {
    renewConnection: 'Renew connection',
    activate: 'Activate',
    reopen: 'Reopen',
    status: 'Status',
    expiresIn: 'Expires in',
    autosave: 'Autosave',
    rentReporting: 'Rent reporting',
};
const BankConnectionInfoCard = ({ data, onReopen, onRenew, onActivate, savingsPlanConnectionId, rentReporting, }) => {
    const styles = useStyles(styleSet);
    const expiresInDays = useMemo(() => {
        if (!data.consentExpiresAt)
            return null;
        const days = Math.max(0, differenceInDays(new Date(data.consentExpiresAt), new Date()));
        return days;
    }, [data.consentExpiresAt]);
    const button = useMemo(() => {
        if (data.deactivatedOverQuota) {
            return <Button brandReversed square medium title={strings.activate} onPress={onActivate}/>;
        }
        if (data.isClosed) {
            return <Button brandReversed square medium title={strings.reopen} onPress={onReopen}/>;
        }
        if (canRenewConnection(data)) {
            return (<Button {...(isConnectionBroken(data) ? { errorReversed: true } : { brandReversed: true })} square medium title={strings.renewConnection} onPress={onRenew} preventDoubleTap/>);
        }
        return null;
    }, [data, onReopen, onActivate, onRenew]);
    const showStatusMessage = !data.deactivatedOverQuota && data.statusMessage != null;
    return (<View style={styles.container}>
      <View style={[styles.rowInfo, styles.rowInfoNoPadding]}>
        <Text TextThin-14 Secondary>
          {strings.status}
        </Text>
        <BankConnectionStatus data={data}/>
      </View>

      {savingsPlanConnectionId === data.id && (<View style={styles.rowInfo}>
          <Text TextThin-14 Secondary>
            {strings.autosave}
          </Text>
          <Status activeText="Linked" inactiveText="Linked" active/>
        </View>)}

      {rentReporting ? (<View style={styles.rowInfo}>
          <Text TextThin-14 Secondary>
            {strings.rentReporting}
          </Text>
          <RentReportingStatus active={Boolean(rentReporting.reportingAgencies?.length && rentReporting.isTracking)}/>
        </View>) : null}

      {expiresInDays !== null && (<View style={styles.rowInfo}>
          <Text TextThin-14 Secondary>
            {strings.expiresIn}
          </Text>
          <Text Text-14>
            {expiresInDays} {formatPlural('day', 'days', expiresInDays)}
          </Text>
        </View>)}

      {showStatusMessage ? (<View style={styles.statusMessage}>
          <Text TextThin-14 {...(isConnectionBroken(data) ? { Negative: true } : { Secondary: true })}>
            {data.statusMessage}
          </Text>
        </View>) : null}

      {button ? <View style={showStatusMessage ? null : styles.button}>{button}</View> : null}
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        marginHorizontal: rem(16),
        borderRadius: rem(16),
        backgroundColor: colors.cards.onDark,
        padding: rem(16),
        gap: rem(16),
    },
    rowCentered: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    rowInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: rem(12),
    },
    rowInfoNoPadding: {
        paddingTop: 0,
    },
    expanded: {
        transform: [{ rotate: '180deg' }],
    },
    statusMessage: {
        paddingTop: rem(8),
    },
    button: {
        paddingTop: rem(8),
    },
}));
export default BankConnectionInfoCard;
